import React, { useState } from 'react'
import { Play, Edit, Trash2, Archive, RotateCcw, MessageCircle, Globe, FileText, Star, Clock, Tag } from 'lucide-react'

/**
 * 统一的作品卡片组件
 * 用于展示故事、角色、世界，保持统一的尺寸和格式
 */
export const UnifiedCard = ({ 
  item, 
  type, // 'story' | 'character' | 'world'
  onPlay,
  onClick, // 点击卡片（跳转到详情页）
  onEdit,
  onDelete,
  onArchive,
  onRestore,
  onPublish,
  getStatusBadge,
  formatTime,
  characterState, // 角色状态（仅角色需要）
  autoDeleteDays, // 自动删除天数（用于显示倒计时）
  getRemainingDays // 计算剩余天数的函数
}) => {
  // 图片加载状态
  const [imageError, setImageError] = useState(false)
  // 获取卡片数据
  const getCardData = () => {
    switch (type) {
      case 'story':
        // 尝试从故事内容中提取第一张图片作为封面
        let storyImage = item.coverImage || item.thumbnail
        if (!storyImage && item.content) {
          try {
            const contentData = typeof item.content === 'string' ? JSON.parse(item.content) : item.content
            if (contentData?.nodes && Array.isArray(contentData.nodes)) {
              // 查找第一个有图片的节点
              const firstNodeWithImage = contentData.nodes.find(node => 
                node.data?.backgroundImage || 
                node.data?.generatedImage || 
                node.data?.sceneImage
              )
              if (firstNodeWithImage) {
                storyImage = firstNodeWithImage.data.backgroundImage || 
                            firstNodeWithImage.data.generatedImage || 
                            firstNodeWithImage.data.sceneImage
              }
            }
          } catch (e) {
            // 解析失败，使用默认
          }
        }
        
        return {
          id: item.id,
          title: item.title,
          description: item.description || '暂无描述',
          image: storyImage,
          status: item.status,
          tags: item.tags || [],
          meta: [
            { icon: Clock, text: `更新于 ${formatTime(item.updatedAt)}` },
            { icon: FileText, text: item.genre || '未分类' }
          ],
          playLabel: '预览',
          editLabel: '编辑'
        }
      case 'character':
        return {
          id: item.id,
          title: item.name,
          description: item.description || '暂无描述',
          image: item.avatar,
          status: item.isPublic ? 'published' : (item.status === 'trash' ? 'trash' : (item.status || 'draft')),
          tags: item.tags || [],
          meta: characterState ? [
            { icon: MessageCircle, text: `好感度: ${characterState.favorability || 0}` },
            { icon: Clock, text: `最后聊天: ${formatTime(characterState.lastChatTime)}` }
          ] : [],
          playLabel: '聊天',
          editLabel: null
        }
      case 'world':
        return {
          id: item.worldId || item.id,
          title: item.name,
          description: item.description || item.summary || '暂无描述',
          image: item.coverImage,
          status: item.isPublic ? 'published' : (item.status === 'trash' ? 'trash' : 'draft'),
          tags: item.tags || [],
          meta: [
            { icon: Play, text: `${item.playCount || 0} 次游玩` },
            { icon: Star, text: `${item.rating || 0} 评分` }
          ],
          playLabel: '查看',
          editLabel: null
        }
      default:
        return null
    }
  }

  const cardData = getCardData()
  if (!cardData) return null

  const isTrash = cardData.status === 'trash'
  const isPublished = cardData.status === 'published'
  const canPublish = Boolean(onPublish) && !isTrash && !isPublished
  
  // 重置图片错误状态当图片URL改变时
  React.useEffect(() => {
    setImageError(false)
  }, [cardData.image])

  return (
    <div 
      className="bg-dark-800 rounded-xl overflow-hidden border border-dark-700 hover:border-primary/50 transition-all group h-full flex flex-col cursor-pointer"
      onClick={() => {
        // 如果提供了 onClick，优先使用（跳转到详情页）
        if (onClick) {
          onClick(item, type)
        } else if (onPlay && !isTrash) {
          // 否则使用 onPlay（直接进入体验）
          onPlay(item)
        }
      }}
    >
      {/* 图片区域 - 统一高度 */}
      <div className="relative h-48 bg-gradient-to-br from-primary/20 to-secondary/20 overflow-hidden">
        {cardData.image && !imageError ? (
          <img
            src={cardData.image}
            alt={cardData.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              // 静默处理错误，阻止错误传播到控制台
              try {
                e.preventDefault()
                e.stopPropagation()
              } catch (err) {
                // 忽略错误处理中的错误
              }
              setImageError(true)
              // 移除图片元素，避免继续尝试加载
              e.target.style.display = 'none'
              // 清除 src 属性，防止浏览器继续尝试加载
              e.target.src = ''
            }}
            onLoad={() => {
              // 图片加载成功，确保错误状态重置
              setImageError(false)
            }}
            loading="lazy"
            // 使用 referrerPolicy 来避免某些 CORS 问题
            referrerPolicy="no-referrer"
          />
        ) : null}
        {/* 占位符 - 当没有图片或图片加载失败时显示 */}
        <div 
          className={`absolute inset-0 flex items-center justify-center ${
            cardData.image && !imageError ? 'hidden' : ''
          }`}
        >
          {type === 'story' && <FileText className="w-16 h-16 text-primary/30" />}
          {type === 'character' && <MessageCircle className="w-16 h-16 text-primary/30" />}
          {type === 'world' && <Globe className="w-16 h-16 text-primary/30" />}
        </div>
        
        {/* 状态徽章 - 右上角 */}
        {cardData.status && getStatusBadge && (
          <div className="absolute top-2 right-2">
            {getStatusBadge(cardData.status, type)}
          </div>
        )}
      </div>

      {/* 内容区域 */}
      <div className="p-4 flex-1 flex flex-col">
        {/* 标题 */}
        <h3 className="text-lg font-bold text-white group-hover:text-primary transition-colors mb-2 line-clamp-1">
          {cardData.title}
        </h3>

        {/* 描述 */}
        <p className="text-sm text-dark-300 mb-3 line-clamp-2 flex-1">
          {cardData.description}
        </p>

        {/* 标签 */}
        {cardData.tags && cardData.tags.length > 0 && (
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Tag className="w-3 h-3 text-primary" />
            {cardData.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
            {cardData.tags.length > 3 && (
              <span className="text-xs text-dark-400">+{cardData.tags.length - 3}</span>
            )}
          </div>
        )}

        {/* 元信息 */}
        {cardData.meta.length > 0 && (
          <div className="flex items-center gap-4 text-xs text-dark-400 mb-4 flex-wrap">
            {cardData.meta.map((meta, index) => (
              <div key={index} className="flex items-center gap-1">
                <meta.icon className="w-4 h-4" />
                <span>{meta.text}</span>
              </div>
            ))}
          </div>
        )}
        
        {/* 回收站信息：显示删除时间和剩余天数 */}
        {isTrash && item.deletedAt && (
          <div className="mb-4 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="text-xs text-red-400 mb-1">
              删除时间：{formatTime(item.deletedAt)}
            </div>
            {autoDeleteDays > 0 && getRemainingDays && (
              (() => {
                const remaining = getRemainingDays(item.deletedAt, autoDeleteDays)
                return remaining !== null && (
                  <div className={`text-xs ${remaining <= 3 ? 'text-red-400' : remaining <= 7 ? 'text-yellow-400' : 'text-dark-300'}`}>
                    {remaining === 0 
                      ? '⚠️ 即将自动删除' 
                      : remaining <= 3 
                        ? `⚠️ ${remaining}天后自动删除` 
                        : `${remaining}天后自动删除`}
                  </div>
                )
              })()
            )}
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex gap-2 mt-auto">
          {canPublish && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onPublish(item, type)
              }}
              className="flex-1 px-3 py-2 bg-secondary/20 hover:bg-secondary/30 text-secondary rounded-lg transition-colors text-sm flex items-center justify-center gap-1"
            >
              发布
            </button>
          )}
          {onPlay && !isTrash && (
            <button
              onClick={(e) => {
                e.stopPropagation() // 阻止事件冒泡，避免触发卡片的 onClick
                onPlay(item)
              }}
              className="flex-1 px-3 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors text-sm flex items-center justify-center gap-1"
            >
              <Play className="w-4 h-4" />
              {cardData.playLabel}
            </button>
          )}

          {/* 删除按钮：非回收站时移到回收站，回收站时永久删除 */}
          {onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation() // 阻止事件冒泡，避免触发卡片的 onClick
                onDelete(item, type, isTrash)
              }}
              className={`flex-1 px-3 py-2 rounded-lg transition-colors text-sm flex items-center justify-center gap-1 ${
                isTrash
                  ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400'
                  : 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400'
              }`}
              title={isTrash ? '永久删除' : '移至回收站'}
            >
              <Trash2 className="w-4 h-4" />
              {isTrash ? '永久删除' : '删除'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

